/**
 * Shared React Query config for the per-module email connection status
 * (`GET /workspaces/{ws}/integrations/email/status?moduleId=…`).
 *
 * Before #5563 this endpoint was hit twice, independently, on every Email
 * module mount: ModuleRenderer ran a TanStack query purely to gate menu items,
 * while EmailPanel's `useEmailStatus` ran its own uncached fetch for the panel's
 * connection/account state. Because the two used different mechanisms they could
 * not share an in-flight request or a cache entry, so each entry into Email cost
 * two round trips.
 *
 * Both readers now build their query from THIS module so they land on one shared
 * cache entry (`emailStatusQueryKey`) with one identical `queryFn` — a single
 * request per module mount serves both. Keep the key + queryFn identical between
 * every call site: a divergent key re-splits the cache and reintroduces the
 * double fetch, and a divergent queryFn makes the shared entry non-deterministic.
 *
 * The cache holds the RAW `EmailStatusResponse` (what `getEmailStatus` returns).
 * That matches the chat-pane reader in `features/chat/components/ChatBox.tsx`,
 * which reads the same key, and lets `getEmailStatus` throw on a failed probe
 * exactly like that reader's raw fetch does. `useEmailStatus` normalizes to the
 * panel's `EmailStatus` shape on read via `normalizeEmailStatus`.
 */
import { getEmailStatus, type EmailStatusResponse } from '@/lib/api/endpoints/email-module';
import type { EmailStatus } from './types';

/** THE cache key both status readers share, keyed by module instance id. */
export function emailStatusQueryKey(moduleId: string) {
  return ['email-status-module', moduleId] as const;
}

/**
 * Shared `{ queryKey, queryFn, retry }` for `useQuery` / `fetchQuery`.
 *
 * `retry: false` keeps the probe one-shot (as the module's raw fetches always
 * were) so a failed status check doesn't fan out into a retry storm and the
 * request count stays deterministic. Callers add their own `staleTime` /
 * `enabled` / `select`.
 */
export function emailStatusQueryOptions(moduleId: string, authToken?: string | null) {
  return {
    queryKey: emailStatusQueryKey(moduleId),
    queryFn: () => getEmailStatus({ moduleId }, authToken),
    retry: false as const,
  };
}

/**
 * Normalize the raw `/status` response into the panel's `EmailStatus` shape.
 * Byte-faithful to the pre-#5563 `useEmailStatus` mapping (`account || null`,
 * `accounts || []`). Returns `null` for absent data so it composes as a
 * `useQuery` `select` and as a fallback in the imperative fetch path.
 */
export function normalizeEmailStatus(data: EmailStatusResponse | undefined): EmailStatus | null {
  if (!data) return null;
  return {
    connected: data.connected,
    account: (data.account as string | null) || null,
    accounts: (data.accounts || []) as unknown as EmailStatus['accounts'],
  };
}
